import { MoonIcon, RefreshCwOffIcon } from "lucide-react";
import type { QueryClient } from "react-query";
import { permittedOrganizations } from "#/api/queries/organizations";
import { templates } from "#/api/queries/templates";
import { users } from "#/api/queries/users";
import type { WorkspaceStatus } from "#/api/typesGenerated";
import { Avatar } from "#/components/Avatar/Avatar";
import type { FilterOption } from "#/components/Filter/FilterCombobox/types";
import { StatusIndicatorDot } from "#/components/StatusIndicator/StatusIndicator";
import { variantByStatusType } from "#/modules/workspaces/WorkspaceStatusIndicator/WorkspaceStatusIndicator";
import { getDisplayWorkspaceStatus } from "#/utils/workspace";

// User suggestions are capped; the picker is a prefix search, not a full list.
const USER_SUGGESTIONS_LIMIT = 25;

/** The slice of `QueryClient` the option loaders depend on. */
export type OptionsQueryClient = Pick<QueryClient, "fetchQuery">;

const STATUS_OPTIONS: WorkspaceStatus[] = [
	"running",
	"stopped",
	"failed",
	"pending",
];

export const getStatusFilterOptions = async (
	query: string,
): Promise<FilterOption[]> => {
	const normalized = query.trim().toLowerCase();
	const options = STATUS_OPTIONS.map((status) => {
		const display = getDisplayWorkspaceStatus(status);
		return {
			label: display.text,
			value: status,
			startIcon: (
				<StatusIndicatorDot
					variant={variantByStatusType[display.type]}
					size="md"
				/>
			),
		} satisfies FilterOption;
	});

	if (normalized.length === 0) {
		return options;
	}

	return options.filter(
		(option) =>
			option.label.toLowerCase().includes(normalized) ||
			option.value.toLowerCase().includes(normalized),
	);
};

export const getTemplateFilterOptions = async (
	query: string,
	queryClient: OptionsQueryClient,
): Promise<FilterOption[]> => {
	// Fetch through the shared `templates` query so the dropdown and the page's
	// own template list read from one cache instead of diverging.
	const allTemplates = await queryClient.fetchQuery(templates());
	const normalized = query.trim().toLowerCase();
	const filtered =
		normalized.length === 0
			? allTemplates
			: allTemplates.filter(
					(template) =>
						template.name.toLowerCase().includes(normalized) ||
						template.display_name.toLowerCase().includes(normalized),
				);

	return filtered.map((template) => ({
		label: template.display_name || template.name,
		value: template.name,
		startIcon: (
			<Avatar
				size="sm"
				variant="icon"
				src={template.icon}
				fallback={template.display_name || template.name}
			/>
		),
	}));
};

type UserIdentity = Readonly<{ username: string; avatar_url?: string }>;

// The current user's own option. Commits the backend's per-session `me`
// sentinel (`user:me` / `owner:me`), matching the page's fallback filter,
// rather than a static `<username>`.
const selfUserOption = (me: UserIdentity): FilterOption => ({
	label: `${me.username} (you)`,
	appliedLabel: "me",
	value: "me",
	startIcon: <Avatar fallback={me.username} src={me.avatar_url} size="sm" />,
});

// Users who cannot list other users still filter by themselves, so the Owner
// category stays available with just the "you" option.
export const getSelfUserFilterOptions = async (
	query: string,
	me: UserIdentity,
): Promise<FilterOption[]> => {
	const option = selfUserOption(me);
	const normalized = query.trim().toLowerCase();
	if (
		normalized.length === 0 ||
		option.label.toLowerCase().includes(normalized) ||
		option.value.includes(normalized)
	) {
		return [option];
	}
	return [];
};

export const getUserFilterOptions = async (
	query: string,
	me: UserIdentity,
	queryClient: OptionsQueryClient,
): Promise<FilterOption[]> => {
	const usersRes = await queryClient.fetchQuery(
		users({ q: query, limit: USER_SUGGESTIONS_LIMIT }),
	);
	// The users API also matches name and email, so a result for the current
	// user keeps its option even when the query is not in its label.
	const self = usersRes.users.some((user) => user.username === me.username)
		? [selfUserOption(me)]
		: await getSelfUserFilterOptions(query, me);
	const options = usersRes.users
		.filter((user) => user.username !== me.username)
		.map<FilterOption>((user) => ({
			label: user.username,
			value: user.username,
			startIcon: (
				<Avatar fallback={user.username} src={user.avatar_url} size="sm" />
			),
		}));

	return [...self, ...options];
};

type AttributeDefinition = {
	label: string;
	value: string;
	icon: React.ReactNode;
	/** Hidden when the deployment lacks the entitlement gating this attribute. */
	requiresDormantEntitlement: boolean;
};

const ATTRIBUTE_DEFINITIONS: readonly AttributeDefinition[] = [
	{
		label: "Outdated",
		value: "outdated",
		icon: <RefreshCwOffIcon />,
		requiresDormantEntitlement: false,
	},
	{
		label: "Dormant",
		value: "dormant",
		icon: <MoonIcon />,
		requiresDormantEntitlement: true,
	},
];

/**
 * Query keys the Attributes category owns, derived from its option definitions
 * so a new attribute cannot commit a chip token the parser silently drops.
 */
export const ATTRIBUTE_CHIP_KEYS: readonly string[] = ATTRIBUTE_DEFINITIONS.map(
	(attribute) => attribute.value,
);

/**
 * Boolean workspace attributes exposed as a single "Attributes" category. Each
 * option commits its own `key:true` chip (e.g. `outdated:true`) rather than a
 * shared `attribute:` key, matching the backend workspace search filters.
 */
export const getAttributeFilterOptions = async (
	query: string,
	options: Readonly<{ canFilterDormant: boolean }>,
): Promise<FilterOption[]> => {
	const normalized = query.trim().toLowerCase();

	return ATTRIBUTE_DEFINITIONS.filter(
		(attribute) =>
			!attribute.requiresDormantEntitlement || options.canFilterDormant,
	)
		.filter(
			(attribute) =>
				normalized.length === 0 ||
				attribute.label.toLowerCase().includes(normalized) ||
				attribute.value.toLowerCase().includes(normalized),
		)
		.map((attribute) => ({
			label: attribute.label,
			value: attribute.value,
			token: `${attribute.value}:true`,
			startIcon: attribute.icon,
		}));
};

export const getOrganizationFilterOptions = async (
	query: string,
	queryClient: OptionsQueryClient,
): Promise<FilterOption[]> => {
	// Reuse the shared `permittedOrganizations` query, which fetches the org list
	// and applies the `audit_log:read` authorization gate, rather than duplicating
	// that logic under a private key.
	// TODO(DEVEX-421 follow-up): the `audit_log:read` gate is carried over from the
	// old `useOrganizationsFilterMenu` and is wrong for workspace filtering. A
	// plain member sees an empty org list here. Replace it with a workspace-read /
	// org-membership check.
	const permitted = await queryClient.fetchQuery(
		permittedOrganizations({
			object: { resource_type: "audit_log" },
			action: "read",
		}),
	);

	const normalized = query.trim().toLowerCase();
	const mapped = permitted.map((organization) => ({
		label: organization.display_name || organization.name,
		value: organization.name,
		startIcon: (
			<Avatar
				key={organization.id}
				size="sm"
				fallback={organization.display_name || organization.name}
				src={organization.icon}
			/>
		),
	}));

	if (normalized.length === 0) {
		return mapped;
	}
	return mapped.filter(
		(option) =>
			option.label.toLowerCase().includes(normalized) ||
			option.value.toLowerCase().includes(normalized),
	);
};
